/**
 * Import documents (2026-10-19): bring many files in at once as drafts.
 *
 * Pick the files, plus an optional list (docvault-import.json) that gives each
 * file its title, type, area, company and notes. Files not on the list get a
 * type from their name and need an area chosen here. Each file becomes a draft
 * exactly as if it had gone through New document: a number is assigned, the
 * file goes straight to S3, nothing is submitted or published. Files whose
 * title already exists in that company (archived and retired documents too)
 * are skipped, so a second run after an interruption doesn't make duplicates.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, putFile, sha256, type DocView } from "../lib/api";
import { useStore } from "../lib/store";
import { AREA_CODES, AREA_LABELS, TYPE_CODES, TYPE_LABELS, VIDEO_TYPE_CODES, MAX_FILE_BYTES, MAX_VIDEO_BYTES, isVideoFile, coName } from "../lib/brand";
import { cleanTitle, guessType } from "../lib/extract";
import { bytes } from "../lib/format";
import { Empty } from "../components/bits";

interface ManifestEntry { file: string; title?: string; type?: string; area?: string; company?: string; notes?: string; description?: string }
type Status = "ready" | "exists" | "invalid" | "working" | "done" | "failed";
interface Row {
  key: string; file: File; title: string; type: string; area: string; company: string; notes: string; description: string;
  fromList: boolean; include: boolean; status: Status; message: string; number?: string; docId?: string;
}

/** Codes retired by ADR-0016 (2026-10-22) and what they became. */
const OLD_TYPES: Record<string, string> = { PRC: "SOP", PRS: "PRO", GDL: "GUI", TPL: "TMP" };
/** "SOP - SOP" or "sop" -> "SOP". Old manifests (MSA Creator) still use the pre-ADR-0016 codes. */
const code = (v: string | undefined, allowed: readonly string[], kind: "type" | "area" | "other" = "other") => {
  const raw = String(v ?? "").trim();
  let c = raw.split(/[\s-]/)[0]!.toUpperCase();
  if (kind === "type") c = OLD_TYPES[c] ?? c;
  // "SD - Service desk" was the old service desk area; it is Operations now (SD = Software Development).
  if (kind === "area" && /service ?desk/i.test(raw)) c = "OPS";
  return allowed.includes(c) ? c : "";
};
/** Title from a built file name: "SOP-Map-Network-Drive-ITAssurance-20261019.docx" -> "Map Network Drive". */
function titleFromName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").replace(/^[A-Z]{2,5}-/, "")
    .replace(/-(ITAssurance|Balancelogic|ConsultraCloud|Generate)(-\d{8})?$/i, "").replace(/-\d{8}$/, "");
  return cleanTitle(base.replace(/-/g, " "));
}

export function ImportPage() {
  const { p, docs, reload, toast, me } = useStore();
  const companies = [...p.myCodes.filter((c) => p.canAuthor(c)), ...(p.canAuthor("CPY") ? ["CPY"] : [])];
  const [rows, setRows] = useState<Row[]>([]);
  const [listName, setListName] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const fi = useRef<HTMLInputElement>(null);

  // Archived and retired titles count too (they load separately; authors can see them).
  const [gone, setGone] = useState<DocView[]>([]);
  useEffect(() => { api.archivedDocuments().then((r) => setGone(r.documents), () => undefined); }, []);
  const existing = useMemo(() => {
    const s = new Map<string, string>();
    for (const d of gone) s.set(`${d.company}|${d.title.trim().toLowerCase()}`, d.status);
    for (const d of docs ?? []) s.set(`${d.company}|${d.title.trim().toLowerCase()}`, d.status);
    return s;
  }, [docs, gone]);

  const check = (r: Row): Row => {
    if (["working", "done"].includes(r.status)) return r;
    const video = isVideoFile(r.file.type, r.file.name);
    let message = "";
    if (!r.title.trim()) message = "Needs a title";
    else if (!r.type) message = "Pick a type";
    else if (!r.area) message = "Pick an area";
    else if (!r.company || !companies.includes(r.company)) message = "You can't add documents to that company";
    else if (video && !VIDEO_TYPE_CODES.includes(r.type)) message = "Videos can be How-Tos or SOPs";
    else if (r.file.size > (video ? MAX_VIDEO_BYTES : MAX_FILE_BYTES)) message = video ? "Over 1 GB" : "Over 100 MB";
    if (message) return { ...r, status: "invalid", message, include: false };
    const found = existing.get(`${r.company}|${r.title.trim().toLowerCase()}`);
    if (found) return { ...r, status: "exists", message: found === "archived" || found === "retired" ? `Already in the library (${found})` : "Already in the library", include: r.status === "exists" ? r.include : false };
    return { ...r, status: r.status === "failed" ? "failed" : "ready", message: r.status === "failed" ? r.message : "", include: r.status === "invalid" || r.status === "exists" ? true : r.include };
  };

  const addFiles = async (list: FileList | File[]) => {
    const all = Array.from(list);
    const jsonFile = all.find((f) => /\.json$/i.test(f.name));
    let manifest: ManifestEntry[] = [];
    let defaultCo = "";
    if (jsonFile) {
      try {
        const m = JSON.parse(await jsonFile.text()) as { company?: string; documents?: ManifestEntry[] } | ManifestEntry[];
        manifest = Array.isArray(m) ? m : m.documents ?? [];
        defaultCo = Array.isArray(m) ? "" : String(m.company ?? "");
        setListName(`${jsonFile.name} (${manifest.length} entries)`);
      } catch {
        toast(`${jsonFile.name} isn't a valid import list.`);
      }
    }
    const byName = new Map(manifest.map((e) => [e.file.toLowerCase(), e]));
    const fallbackCo = companies.find((c) => c !== "CPY") ?? companies[0] ?? "";
    const fresh: Row[] = all.filter((f) => f !== jsonFile).map((f) => {
      const e = byName.get(f.name.toLowerCase());
      const title = e?.title?.trim() || titleFromName(f.name);
      const type = code(e?.type, TYPE_CODES, "type") || code(f.name.split("-")[0], TYPE_CODES, "type") || guessType(title, f.name) || "";
      return check({
        key: `${f.name}|${f.size}|${f.lastModified}`, file: f, title, type,
        area: code(e?.area, AREA_CODES, "area"), company: code(e?.company || defaultCo, [...companies]) || fallbackCo,
        notes: e?.notes?.trim() ?? "", description: e?.description?.trim() ?? "",
        fromList: !!e, include: true, status: "ready", message: "",
      });
    });
    setRows((old) => {
      const keys = new Set(old.map((r) => r.key));
      return [...old, ...fresh.filter((r) => !keys.has(r.key))].sort((a, b) => a.file.name.localeCompare(b.file.name));
    });
  };

  // Check the list again once archived titles arrive or the library reloads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRows((rs) => rs.map(check)); }, [existing]);
  const edit = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? check({ ...r, ...patch, status: r.status === "failed" ? "ready" : r.status }) : r)));
  const queue = rows.filter((r) => r.include && (r.status === "ready" || r.status === "failed" || r.status === "exists"));
  const counts = {
    ready: rows.filter((r) => r.status === "ready").length,
    exists: rows.filter((r) => r.status === "exists").length,
    invalid: rows.filter((r) => r.status === "invalid").length,
    done: rows.filter((r) => r.status === "done").length,
    failed: rows.filter((r) => r.status === "failed").length,
  };

  const run = async () => {
    setRunning(true);
    let ok = 0, bad = 0;
    for (const r of queue) {
      setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, status: "working", message: "Uploading…" } : x)));
      try {
        const c = await api.createDocument({
          company: r.company, type: r.type, area: r.area, title: r.title.trim(), description: r.description || undefined,
          file_name: r.file.name, content_type: r.file.type || "application/octet-stream", file_size: r.file.size,
          notes: r.notes || "Initial release in DocVault.",
        });
        await putFile(c.upload.url, r.file);
        await api.confirmUpload(c.version.id, await sha256(r.file));
        ok++;
        setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, status: "done", message: "Draft created", number: c.document.base_number, docId: c.document.id, include: false } : x)));
      } catch (e) {
        bad++;
        setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, status: "failed", message: (e as Error).message || "Didn't import" } : x)));
      }
    }
    setRunning(false);
    await reload();
    toast(`Imported ${ok} draft${ok === 1 ? "" : "s"}${bad ? `; ${bad} didn't import (see the list)` : ""}.`);
  };

  if (!companies.length) return <div className="card empty view"><h3>Not available</h3>Only authors can import documents.</div>;

  return (
    <div className="view">
      <div className="hgroup"><div>
        <div className="eyebrow">Bring documents in</div>
        <h1 className="title">Import <em>several</em> documents</h1>
        <div className="lede">Each file becomes a draft with its own number, exactly as if you'd used New document. Nothing is submitted or published. You review and submit each one when it's ready.</div>
      </div></div>

      <div className={`card drop imp-drop ${over ? "over" : ""}`} tabIndex={0} role="button"
        onClick={() => fi.current?.click()} onKeyDown={(e) => { if (e.key === "Enter") fi.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void addFiles(e.dataTransfer.files); }}>
        <span className="nd-up">↑</span>
        <div><b>Drop the files here</b> or click to choose them (select them all at once)
          <div className="help">Add <b>docvault-import.json</b> with them to fill in each title, type, area and company. Without it, the type comes from the file name and you pick the area below.{listName ? <> Using <b>{listName}</b>.</> : null}</div></div>
        <input type="file" ref={fi} hidden multiple onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {rows.length > 0 && (
        <>
          <div className="imp-bar">
            <span className="pill pub">{counts.ready} ready</span>
            {counts.exists > 0 && <span className="pill draft">{counts.exists} already in the library (skipped)</span>}
            {counts.invalid > 0 && <span className="pill chg">{counts.invalid} need details</span>}
            {counts.done > 0 && <span className="pill rev">{counts.done} imported</span>}
            {counts.failed > 0 && <span className="pill chg">{counts.failed} failed</span>}
            <span style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" disabled={running} onClick={() => { setRows([]); setListName(null); }}>Clear the list</button>
            <button className={`btn btn-accent btn-sm ${running ? "busy" : ""}`} disabled={running || queue.length === 0} onClick={() => void run()}>
              {running ? "Importing…" : `Import ${queue.length} as drafts`}
            </button>
          </div>
          <div className="card tbl-wrap">
            <table className="tbl imp-tbl">
              <colgroup><col style={{ width: 34 }} /><col /><col style={{ width: 170 }} /><col style={{ width: 180 }} /><col style={{ width: 110 }} /><col style={{ width: 190 }} /></colgroup>
              <thead><tr><th /><th>Title and file</th><th>Type</th><th>Area</th><th>Company</th><th>Status</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const locked = running || r.status === "done" || r.status === "working";
                  return (
                    <tr key={r.key} className={`imp-${r.status}`}>
                      <td><input type="checkbox" aria-label={`Import ${r.title}`} checked={r.include} disabled={locked || r.status === "invalid"}
                        onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, include: e.target.checked } : x)))} /></td>
                      <td>
                        <input className="inp inp-sm" value={r.title} disabled={locked} onChange={(e) => edit(r.key, { title: e.target.value })} aria-label="Title" />
                        <div className="help imp-file" title={r.file.name}>{r.file.name} · {bytes(r.file.size)}{r.fromList ? "" : " · not on the list"}</div>
                      </td>
                      <td><select className="inp inp-sm" value={r.type} disabled={locked} onChange={(e) => edit(r.key, { type: e.target.value })} aria-label="Type">
                        <option value="">Choose…</option>{TYPE_CODES.map((t) => <option key={t} value={t}>{t} · {TYPE_LABELS[t]}</option>)}</select></td>
                      <td><select className="inp inp-sm" value={r.area} disabled={locked} onChange={(e) => edit(r.key, { area: e.target.value })} aria-label="Area">
                        <option value="">Choose…</option>{AREA_CODES.map((a) => <option key={a} value={a}>{a} · {AREA_LABELS[a]}</option>)}</select></td>
                      <td><select className="inp inp-sm" value={r.company} disabled={locked} onChange={(e) => edit(r.key, { company: e.target.value })} aria-label="Company">
                        {companies.map((c) => <option key={c} value={c} title={coName(c)}>{c}</option>)}</select></td>
                      <td className="imp-status">
                        {r.status === "done" && r.docId ? <Link to={`/doc/${r.docId}`} className="docno">{r.number}</Link>
                          : <span>{r.status === "ready" ? (r.include ? "Ready" : "Skipped") : r.message}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="foot-note">Drafts are owned by {me.user.display_name}. Imported drafts show under My drafts; submit each for approval when it's reviewed.</div>
        </>
      )}
      {rows.length === 0 && <div className="card" style={{ marginTop: 14 }}><Empty title="No files yet">Choose the files to import, with their docvault-import.json list if you have one.</Empty></div>}
    </div>
  );
}
